// Underground parking, drawn: stalls, aisles, the ramp and the cores going down.
//
// Rows run parallel to the street: a 5 m stall row, a 6 m two-way aisle, a
// 5 m stall row, repeated inward. The ramp leaves from the street edge. A
// stall counts only if it lies wholly inside the basement and clear of the
// ramp and of every core, because a core is a stair and lifts going down to
// the car park, and a stall drawn over it is not a stall.
//
// The figure people quote, ~25–30 m² per space, falls out of this: it is
// stall plus its share of aisle, ramp and cores. It is not an input here.
import { area, bbox as bboxOf, covers, difference, distanceTo, edges, grow, inset, intersection, union, type MultiPoly, type Pt } from "./geom.ts";
import type { Envelope } from "./envelope.ts";
import type { Site } from "./rules.ts";

export const STALL = { width: 2.5, length: 5.0 };
export const AISLE = 6.0;
export const RAMP = { width: 6.0, length: 20.0 };
export const BASEMENT_SETBACK = 1.0;
export const BASEMENT_STOREY = 3.0;
export const MAX_BASEMENTS = 4;

export interface ParkingLevel {
  index: number; // -1, -2, ...
  base: number;
  footprint: MultiPoly;
  stalls: MultiPoly[];
  aisles: MultiPoly;
  ramp: MultiPoly;
  cores: MultiPoly;
}

export interface ParkingPlan {
  perLevel: number;
  levels: ParkingLevel[];
  capacity: number;
  /** True when even MAX_BASEMENTS levels do not hold the required spaces. */
  short: boolean;
}

interface Frame { a: Pt; u: Pt; n: Pt }
const frame = (a: Pt, b: Pt): Frame => {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const u: Pt = [(b[0] - a[0]) / len, (b[1] - a[1]) / len];
  return { a, u, n: [-u[1], u[0]] };
};
const at = (f: Frame, s: number, t: number): Pt => [f.a[0] + f.u[0] * s + f.n[0] * t, f.a[1] + f.u[1] * s + f.n[1] * t];
const rect = (f: Frame, s0: number, s1: number, t0: number, t1: number): MultiPoly => [[[at(f, s0, t0), at(f, s1, t0), at(f, s1, t1), at(f, s0, t1)]]];
const local = (f: Frame, p: Pt): Pt => { const dx = p[0] - f.a[0], dy = p[1] - f.a[1]; return [dx * f.u[0] + dy * f.u[1], dx * f.n[0] + dy * f.n[1]]; };

/** The street edge the ramp leaves from: the longest alignment edge, else the longest edge. */
function streetEdge(site: Site, ring: Pt[]): number {
  const len = (i: number) => { const [a, b] = [ring[i], ring[(i + 1) % ring.length]]; return Math.hypot(b[0] - a[0], b[1] - a[1]); };
  const idx = ring.map((_, i) => i);
  const street = idx.filter((i) => site.edges[i] === "aliniament");
  return (street.length ? street : idx).reduce((b, i) => (len(i) > len(b) ? i : b));
}

/** One level's stalls for a given offset of the row pattern. */
function stallsFor(f: Frame, basement: MultiPoly, blocked: MultiPoly, s0: number, s1: number, t0: number, t1: number, ds: number, dt: number) {
  const free = difference(basement, blocked);
  // Inside test without a boolean per stall: all four corners in the free
  // area and no corner of the free area inside the stall. Exact for convex
  // stalls against a polygon, and an order of magnitude cheaper than Clipper.
  const vertices = free.flatMap((poly) => poly.flatMap((ring) => ring));
  const fits = (st: MultiPoly) => {
    const r = st[0][0];
    if (!r.every((p) => covers(p, free))) return false;
    const xs = r.map((p) => local(f, p)[0]), ys = r.map((p) => local(f, p)[1]);
    const [x0, x1, y0, y1] = [Math.min(...xs) + 1e-6, Math.max(...xs) - 1e-6, Math.min(...ys) + 1e-6, Math.max(...ys) - 1e-6];
    if (vertices.some((v) => { const [x, y] = local(f, v); return x > x0 && x < x1 && y > y0 && y < y1; })) return false;
    // A thin obstacle can cross a stall with no corner on either side; near
    // anything blocked, settle it exactly.
    return !near(st) || area(difference(st, free)) < 0.01;
  };
  const boxes = blocked.map((poly) => bboxOf([poly]));
  const near = (st: MultiPoly) => { const b = bboxOf(st); return boxes.some((o) => b.minX < o.maxX && b.maxX > o.minX && b.minY < o.maxY && b.maxY > o.minY); };
  const stalls: MultiPoly[] = [];
  const aisles: MultiPoly[] = [];
  const module = 2 * STALL.length + AISLE;
  for (let t = t0 + dt; t + STALL.length <= t1 + 1e-6; t += module) {
    const rows = [t, t + STALL.length + AISLE];
    aisles.push(rect(f, s0, s1, t + STALL.length, t + STALL.length + AISLE));
    for (const r of rows) {
      if (r + STALL.length > t1 + 1e-6) continue;
      for (let s = s0 + ds; s + STALL.width <= s1 + 1e-6; s += STALL.width) {
        const st = rect(f, s, s + STALL.width, r, r + STALL.length);
        if (fits(st)) stalls.push(st);
      }
    }
  }
  return { stalls, aisles: intersection(union(...aisles), free) };
}

/** The best packing of stalls into a region, rows parallel to the edge a→b. */
export function packStalls(region: MultiPoly, blocked: MultiPoly, a: Pt, b: Pt, offsets = { dt: [0, 2, 4, 6], ds: [0, STALL.width / 2] }): { stalls: MultiPoly[]; aisles: MultiPoly } {
  if (area(region) < STALL.width * STALL.length) return { stalls: [], aisles: [] };
  const f = frame(a, b);
  const pts = region.flatMap((p) => p[0]).map((p) => local(f, p));
  const s0 = Math.min(...pts.map((p) => p[0])), s1 = Math.max(...pts.map((p) => p[0]));
  const t0 = Math.min(...pts.map((p) => p[1])), t1 = Math.max(...pts.map((p) => p[1]));
  // a few offsets of the row pattern; keep the one that fits the most stalls
  let best = { stalls: [] as MultiPoly[], aisles: [] as MultiPoly };
  for (const dt of offsets.dt) for (const ds of offsets.ds) {
    const r = stallsFor(f, region, blocked, s0, s1, t0, t1, ds, dt);
    if (r.stalls.length > best.stalls.length) best = r;
  }
  return best;
}

/** The street edge as two points, for a frame. */
export function streetLine(env: Envelope, site: Site): [Pt, Pt] {
  const ring = env.plot[0][0];
  return edges(ring)[streetEdge(site, ring)];
}

/** Where the ramp down to the basement goes: against the street, at the end of the frontage. */
export function rampZone(env: Envelope, site: Site): MultiPoly {
  const basement = inset(env.plot, BASEMENT_SETBACK);
  const [a, b] = streetLine(env, site);
  const f = frame(a, b);
  const pts = basement.flatMap((p) => p[0]).map((p) => local(f, p));
  const s1 = Math.max(...pts.map((p) => p[0])), t0 = Math.min(...pts.map((p) => p[1]));
  return intersection(rect(f, s1 - RAMP.width - 0.5, s1 - 0.5, t0, t0 + RAMP.length), basement);
}

export function parkingPlan(env: Envelope, site: Site, cores: MultiPoly, required: number): ParkingPlan {
  const basement = inset(env.plot, BASEMENT_SETBACK);
  if (required <= 0 || area(basement) < 100) return { perLevel: 0, levels: [], capacity: 0, short: required > 0 };
  const [a, b] = streetLine(env, site);
  const ramp = rampZone(env, site);
  const best = packStalls(basement, union(ramp, cores), a, b);
  const perLevel = best.stalls.length;
  const needed = perLevel === 0 ? MAX_BASEMENTS : Math.ceil(required / perLevel);
  const count = Math.min(MAX_BASEMENTS, needed);
  const levels: ParkingLevel[] = Array.from({ length: count }, (_, k) => ({
    index: -(k + 1),
    base: -(k + 1) * BASEMENT_STOREY,
    footprint: basement,
    stalls: best.stalls,
    aisles: best.aisles,
    ramp,
    cores,
  }));
  return { perLevel, levels, capacity: perLevel * count, short: needed > MAX_BASEMENTS };
}

/** Surface parking: stalls in what the building, a planted margin and the paths leave of the plot. */
export const FACADE_CLEARANCE = 3.0;
export function surfaceParking(env: Envelope, site: Site, building: MultiPoly, keepClear: MultiPoly): { stalls: MultiPoly[]; aisles: MultiPoly } {
  const region = difference(inset(env.plot, 1.5), grow(building, FACADE_CLEARANCE), keepClear);
  const [a, b] = streetLine(env, site);
  return packStalls(region, [], a, b);
}
